import { doc, getDoc } from "firebase/firestore";
import { firestore as db } from "../../backend/firebaseconfig";

// ─── DEV TESTING OVERRIDE ────────────────────────────────────────────────────
// Set role + email to fetch the name from Firestore as that user.
// Leave all as empty strings ("") to use normal production logic.
const TEST_USER_OVERRIDE = {
  role: "", // e.g. "Adviser"
  email: "", // e.g. "alice.smith@example.com"
};
// ─────────────────────────────────────────────────────────────────────────────

const normalise = (value = "") => String(value).trim().toLowerCase();

const safeEmail = (email = "") =>
  String(email).trim().toLowerCase().replace(/\./g, "_");

const cleanValue = (value) => {
  if (!value) return "";

  if (typeof value === "object" && value.value !== undefined) {
    return cleanValue(value.value);
  }

  return String(value).trim();
};

const getFallbackFirstName = (user = {}) => {
  const displayName = String(user?.displayName || "").trim();
  const email = String(user?.email || "").trim();

  if (displayName) {
    return displayName.split(/\s+/)[0];
  }

  const localPart = email.split("@")[0] || "";
  const cleaned = localPart.split(/[._-]+/).filter(Boolean)[0] || "";

  if (!cleaned) return "";

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
};

const getCurrentUserName = (user = {}) => {
  return (
    cleanValue(user?.displayName) ||
    cleanValue(user?.name) ||
    cleanValue(user?.userName)
  );
};

const getRoleParentDocId = (role = "") => {
  const value = normalise(role);

  if (value === "administrator" || value === "administrators") {
    return "Administrators";
  }

  if (value === "staff") {
    return "Staff";
  }

  if (value === "adviser" || value === "advisers") {
    return "Advisers";
  }

  if (
    value === "external" ||
    value === "external user" ||
    value === "external users"
  ) {
    return "External Users";
  }

  return role;
};

const getNameFromPersonalDetails = (data = {}) => {
  if (!data || typeof data !== "object") {
    return "";
  }

  const preferredName =
    cleanValue(data.preferredName) ||
    cleanValue(data.preferred_name) ||
    cleanValue(data["Preferred Name"]) ||
    cleanValue(data["preferred name"]) ||
    cleanValue(data.knownAs) ||
    cleanValue(data.nameKnownAs);

  if (preferredName) return preferredName;

  const firstName =
    cleanValue(data.firstName) ||
    cleanValue(data.first_name) ||
    cleanValue(data["First Name"]) ||
    cleanValue(data["first name"]) ||
    cleanValue(data.forename) ||
    cleanValue(data.givenName);

  return firstName;
};

export const getPersonalDetailsByRoleAndEmail = async ({ role, email }) => {
  if (!role || !email) {
    // console.warn(
    //   "[Greeting] Skipping Firestore fetch — missing role or email",
    //   { role, email },
    // );
    return null;
  }

  const parentDocId = getRoleParentDocId(role);
//   console.log(
//     "[Greeting] Resolved parent doc ID:",
//     parentDocId,
//     "| raw role:",
//     role,
//   );

  const emailDocIds = [
    String(email).trim(),
    String(email).trim().toLowerCase(),
    safeEmail(email),
  ].filter(Boolean);

  const uniqueEmailDocIds = [...new Set(emailDocIds)];
//   console.log("[Greeting] Will try member doc IDs:", uniqueEmailDocIds);

  for (const memberDocId of uniqueEmailDocIds) {
    try {
      const path = `Authorized Members/${parentDocId}/Data/${memberDocId}/Personal Details/Data`;
    //   console.log("[Greeting] Fetching:", path);

      const ref = doc(
        db,
        "Authorized Members",
        parentDocId,
        "Data",
        memberDocId,
        "Personal Details",
        "Data",
      );

      const snap = await getDoc(ref);

      if (snap.exists()) {
        // console.log("[Greeting] Doc found at:", path, "| data:", snap.data());
        return snap.data();
      } else {
        // console.log("[Greeting] Doc not found at:", path);
      }
    } catch (error) {
    //   console.error(
    //     `[Greeting] Failed fetching Personal Details for ${parentDocId}/${memberDocId}:`,
    //     error,
    //   );
    }
  }

//   console.warn("[Greeting] No Personal Details doc found for", { role, email });
  return null;
};

export const getFirstNameFromUser = async (user = {}) => {
  const role = TEST_USER_OVERRIDE.role || user?.role || user?.userRole || "";
  const email =
    TEST_USER_OVERRIDE.email || user?.email || user?.userEmail || "";

//   console.log("[Greeting] getFirstNameFromUser called with:", {
//     role,
//     email,
//     user,
//   });

  const personalDetails = await getPersonalDetailsByRoleAndEmail({
    role,
    email,
  });

  const databaseName = getNameFromPersonalDetails(personalDetails);
//   console.log(
//     "[Greeting] Name from Personal Details:",
//     databaseName || "(none)",
//   );

  if (databaseName) {
    return databaseName;
  }

  const fallbackFirstName = getFallbackFirstName(user);
//   console.log("[Greeting] Fallback first name:", fallbackFirstName || "(none)");

  if (fallbackFirstName) {
    return fallbackFirstName;
  }

  const currentUserName = getCurrentUserName(user);
//   console.log(
//     "[Greeting] Current user name fallback:",
//     currentUserName || "(none)",
//   );

  return currentUserName;
};

export const formatLondonDateTimeFromTimestamp = (
  timestamp,
  locale = "en-GB",
) => {
  return new Intl.DateTimeFormat(locale, {
    timeZone: "Europe/London",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
    .format(new Date(timestamp))
    .replace(" at ", " • ");
};

export const getLondonHourFromTimestamp = (timestamp) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    hour: "numeric",
    hour12: false,
  }).formatToParts(new Date(timestamp));

  return Number(parts.find((part) => part.type === "hour")?.value || 0);
};

export const getGreetingFromTimestamp = (timestamp, name = "") => {
  const hour = getLondonHourFromTimestamp(timestamp);

  if (hour < 12) return `Good morning${name ? `, ${name}!` : ""}`;
  if (hour < 18) return `Good afternoon${name ? `, ${name}!` : ""}`;
  return `Good evening${name ? `, ${name}!` : ""}`;
};

export const getPortalGreetingDataFromTimestamp = async (
  timestamp,
  user = {},
  locale = "en-GB",
) => {
  const firstName = await getFirstNameFromUser(user);

  const greeting = getGreetingFromTimestamp(timestamp, firstName);

  const formattedDateTime = formatLondonDateTimeFromTimestamp(
    timestamp,
    locale,
  );

  return {
    firstName,
    greeting,
    formattedDateTime,
  };
};
